import {
  API, GET, POST, PUT, DELETE,
} from '../middleware/api';
import { API_URL } from '../config';

function getMessageEndpoint(workshopId, chainId, workshopChainId, orgChainId, workshopBelongsToChain) {
  if (workshopId) {
    if (workshopChainId || workshopBelongsToChain) {
      return { prefix: 'cs_', endpoint: 'message' };
    }
    return { prefix: 'cs_', endpoint: 'vehicle_message' };
  }
  if (chainId) {
    // Use unified cc_message endpoint for chain context
    return { prefix: 'cc_', endpoint: 'message' };
  }
  if (orgChainId) {
    return { prefix: 'fleet_', endpoint: 'message' };
  }
  return { prefix: '', endpoint: 'vehicle_message' };
}

// URL of the "who sees this message" preview. Mirrors CreateChainMessageModal.submitForm:
// the service portal previews through cs_message (workshop members lack a chain
// permission), every other portal through cc_message. organization_id lets the
// backend attribute a fleet customer's preview to the company it is sent as.
export function getAudienceUrl(workshopId, chainId, organizationId) {
  const prefix = workshopId ? 'cs_' : 'cc_';
  const parts = [];
  if (workshopId) parts.push(`workshop_id=${workshopId}`);
  if (chainId) parts.push(`chain_id=${chainId}`);
  if (!workshopId && organizationId && organizationId !== 'all') parts.push(`organization_id=${organizationId}`);
  return `${API_URL}/uiapi/1/${prefix}message/audience/?${parts.join('&')}`;
}

function getMessageResponseEndpoint(workshopId, chainId) {
  if (workshopId) {
    return { prefix: 'cs_', endpoint: 'vehicle_message_response' };
  }
  if (chainId) {
    return { prefix: 'cc_', endpoint: 'message_response' };
  }
  return { prefix: '', endpoint: 'vehicle_message_response' };
}

export function fetchMessages(params, workshopId = undefined, organizationId = undefined, chainId = undefined, workshopChainId = undefined, orgChainId = undefined, workshopBelongsToChain = false) {
  const { prefix, endpoint } = getMessageEndpoint(workshopId, chainId, workshopChainId, orgChainId, workshopBelongsToChain);

  const newParams = {
    ...params,
  };

  if (workshopId) {
    newParams.workshop_id = workshopId;
    if (workshopChainId) {
      newParams.chain_id = workshopChainId;
    }
  }

  if (organizationId) {
    newParams.organization_id = organizationId;
    if (orgChainId) {
      newParams.chain_id = orgChainId;
    }
  }

  if (chainId) {
    newParams.chain_id = chainId;
  }

  return {
    [API]: {
      method: GET,
      url: `${API_URL}/uiapi/1/${prefix}${endpoint}/`,
      types: [
        'FETCH_MESSAGES_REQUEST',
        'FETCH_MESSAGES_SUCCESS',
        'FETCH_MESSAGES_FAIL',
      ],
      params: newParams,
    },
  };
}

// One message through the portal's unified endpoint. Used to open a message
// from an email deep link (?message=<id>) when it is not on the current page,
// and whenever a chain-context message is opened: the retrieve marks it read
// for this user, which the list's "Luettu" column reflects.
export function fetchMessageDetail(messageId, workshopId = undefined, organizationId = undefined, chainId = undefined, workshopChainId = undefined, orgChainId = undefined, workshopBelongsToChain = false) {
  const { prefix, endpoint } = getMessageEndpoint(workshopId, chainId, workshopChainId, orgChainId, workshopBelongsToChain);

  const params = {};
  if (workshopId) {
    params.workshop_id = workshopId;
    if (workshopChainId) {
      params.chain_id = workshopChainId;
    }
  }
  if (organizationId) {
    params.organization_id = organizationId;
    if (orgChainId) {
      params.chain_id = orgChainId;
    }
  }
  if (chainId) {
    params.chain_id = chainId;
  }

  return {
    [API]: {
      method: GET,
      url: `${API_URL}/uiapi/1/${prefix}${endpoint}/${messageId}/`,
      types: [
        'FETCH_MESSAGE_DETAIL_REQUEST',
        'FETCH_MESSAGE_DETAIL_SUCCESS',
        'FETCH_MESSAGE_DETAIL_FAIL',
      ],
      params,
    },
  };
}

export function fetchVehicleMessageHistory(chainId, registrationNumbers, dateFrom, dateTo) {
  const regParam = (registrationNumbers || []).join(',');
  return {
    [API]: {
      method: GET,
      url: `${API_URL}/uiapi/1/cc_message/vehicle_history/`,
      types: [
        'FETCH_VEHICLE_MESSAGE_HISTORY_REQUEST',
        'FETCH_VEHICLE_MESSAGE_HISTORY_SUCCESS',
        'FETCH_VEHICLE_MESSAGE_HISTORY_FAIL',
      ],
      params: {
        chain_id: chainId,
        registration_number: regParam,
        date_from: dateFrom,
        date_to: dateTo,
      },
    },
  };
}

export function createMessage(data, workshopId = undefined, chainId = undefined, workshopChainId = undefined, orgChainId = undefined, workshopBelongsToChain = false) {
  const { prefix, endpoint } = getMessageEndpoint(workshopId, chainId, workshopChainId, orgChainId, workshopBelongsToChain);
  const suffixParts = [];
  if (workshopId) { suffixParts.push(`workshop_id=${workshopId}`); }
  if (chainId) { suffixParts.push(`chain_id=${chainId}`); }
  if (workshopChainId && workshopId) { suffixParts.push(`chain_id=${workshopChainId}`); }
  if (orgChainId && !chainId) { suffixParts.push(`chain_id=${orgChainId}`); }
  const suffix = suffixParts.length ? `?${suffixParts.join('&')}` : '';
  const urlExtenstion = data.vehicle_ids && data.vehicle_ids.length ? 'create_batch/' : '';

  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/${prefix}${endpoint}/${urlExtenstion}${suffix}`,
      types: [
        'CREATE_MESSAGE_REQUEST',
        'CREATE_MESSAGE_SUCCESS',
        'CREATE_MESSAGE_FAIL',
      ],
      data,
    },
  };
}

export function createChainMessage(data, chainId) {
  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/cc_message/?chain_id=${chainId}`,
      types: [
        'CREATE_MESSAGE_REQUEST',
        'CREATE_MESSAGE_SUCCESS',
        'CREATE_MESSAGE_FAIL',
      ],
      data,
    },
  };
}

// Service portal (member workshop) chain-context message create. Posts to cs_message,
// whose permission allows workshop members of the chain — cc_message requires a
// chain-level WorkshopChainPermission, which member-workshop users do not have.
export function createCSChainMessage(data, workshopId, chainId) {
  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/cs_message/?workshop_id=${workshopId}&chain_id=${chainId}`,
      types: [
        'CREATE_MESSAGE_REQUEST',
        'CREATE_MESSAGE_SUCCESS',
        'CREATE_MESSAGE_FAIL',
      ],
      data,
    },
  };
}

export function createChainVehicleMessage(data, chainId) {
  const urlExtension = data.vehicle_ids && data.vehicle_ids.length ? 'create_batch/' : '';
  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/cc_vehicle_message/${urlExtension}?chain_id=${chainId}`,
      types: [
        'CREATE_MESSAGE_REQUEST',
        'CREATE_MESSAGE_SUCCESS',
        'CREATE_MESSAGE_FAIL',
      ],
      data,
    },
  };
}

export function createMessageResponse(data, workshopId = undefined, chainId = undefined, organizationId = undefined) {
  const { prefix, endpoint } = getMessageResponseEndpoint(workshopId, chainId);
  let suffix = '';
  if (workshopId) { suffix = `?workshop_id=${workshopId}`; }
  else if (chainId) { suffix = `?chain_id=${chainId}`; }
  else if (organizationId) { suffix = `?organization_id=${organizationId}`; }

  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/${prefix}${endpoint}/${suffix}`,
      types: [
        'CREATE_MESSAGE_RESPONSE_REQUEST',
        'CREATE_MESSAGE_RESPONSE_SUCCESS',
        'CREATE_MESSAGE_RESPONSE_FAIL',
      ],
      data,
    },
  };
}

export function updateMessage(messageId, data, workshopId = undefined, chainId = undefined) {
  const { prefix, endpoint } = getMessageEndpoint(workshopId, chainId);
  let suffix = '';
  if (workshopId) { suffix = `?workshop_id=${workshopId}`; }
  else if (chainId) { suffix = `?chain_id=${chainId}`; }
  return {
    [API]: {
      method: PUT,
      url: `${API_URL}/uiapi/1/${prefix}${endpoint}/${messageId}/${suffix}`,
      types: [
        'UPDATE_MESSAGE_REQUEST',
        'UPDATE_MESSAGE_SUCCESS',
        'UPDATE_MESSAGE_FAIL',
      ],
      data,
    },
  };
}

export function deleteMessage(messageId, workshopId = undefined, chainId = undefined) {
  const { prefix, endpoint } = getMessageEndpoint(workshopId, chainId);
  let suffix = '';
  if (workshopId) { suffix = `?workshop_id=${workshopId}`; }
  else if (chainId) { suffix = `?chain_id=${chainId}`; }
  return {
    [API]: {
      method: DELETE,
      url: `${API_URL}/uiapi/1/${prefix}${endpoint}/${messageId}/${suffix}`,
      types: [
        'DELETE_MESSAGE_REQUEST',
        'DELETE_MESSAGE_SUCCESS',
        'DELETE_MESSAGE_FAIL',
      ],
    },
  };
}

export function approveMessage(messageId, chainId, comment = '') {
  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/cc_message/${messageId}/approve/?chain_id=${chainId}`,
      types: [
        'APPROVE_MESSAGE_REQUEST',
        'APPROVE_MESSAGE_SUCCESS',
        'APPROVE_MESSAGE_FAIL',
      ],
      data: { comment },
    },
  };
}

export function rejectMessage(messageId, chainId, reason = '') {
  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/cc_message/${messageId}/reject/?chain_id=${chainId}`,
      types: [
        'REJECT_MESSAGE_REQUEST',
        'REJECT_MESSAGE_SUCCESS',
        'REJECT_MESSAGE_FAIL',
      ],
      data: { reason },
    },
  };
}

export function forwardMessage(messageId, chainId, data) {
  return {
    [API]: {
      method: POST,
      url: `${API_URL}/uiapi/1/cc_message/${messageId}/forward/?chain_id=${chainId}`,
      types: [
        'FORWARD_MESSAGE_REQUEST',
        'FORWARD_MESSAGE_SUCCESS',
        'FORWARD_MESSAGE_FAIL',
      ],
      data,
    },
  };
}
